// Mixpanel analytics wrapper for Product-Led Growth tracking
import { log } from '@/lib/logger';
import { isDemoMode } from '@/lib/demoUtils';

export type PQLEvent =
  | 'contact_added'
  | 'deal_created'
  | 'meeting_logged'
  | 'pipeline_viewed'
  | 'team_invited'
  | 'bucket_created'
  | 'campaign_created'
  | 'note_added'
  | 'reminder_set'
  | 'email_sent'
  | 'contact_enriched'
  | 'export_generated'
  | 'view_created'
  | 'upgrade_clicked'
  | 'feature_explored';

export interface PQLScore {
  score: number;
  level: 'cold' | 'warm' | 'hot' | 'qualified';
  events: Record<PQLEvent, number>;
}

// PQL Scoring weights
const PQL_WEIGHTS: Record<PQLEvent, number> = {
  contact_added: 5,
  deal_created: 15,
  meeting_logged: 10,
  pipeline_viewed: 3,
  team_invited: 20,
  bucket_created: 8,
  campaign_created: 12,
  note_added: 2,
  reminder_set: 4,
  email_sent: 7,
  contact_enriched: 6,
  export_generated: 5,
  view_created: 4,
  upgrade_clicked: 25,
  feature_explored: 1
};

// PQL Thresholds
const PQL_THRESHOLDS = {
  cold: 0,
  warm: 30,
  hot: 60,
  qualified: 100
};

let isInitialized = false;
let mixpanelClient: MixpanelClient | null = null;
let mixpanelImportPromise: Promise<MixpanelClient | null> | null = null;

interface MixpanelClient {
  init: (token: string, options: Record<string, unknown>) => void;
  track: (eventName: string, properties?: Record<string, unknown>) => void;
  track_pageview: (properties?: Record<string, unknown>) => void;
  identify: (userId: string) => void;
  reset: () => void;
  opt_in_tracking: () => void;
  opt_out_tracking: () => void;
  people: {
    set: (properties: Record<string, unknown>) => void;
    increment: (property: string, value: number) => void;
  };
}

/**
 * Local flag backing the Settings → Account "Usage analytics" switch.
 * Kept outside Mixpanel's own storage so opting out works even before the
 * SDK has ever loaded on this device.
 */
export const ANALYTICS_OPT_OUT_STORAGE_KEY = 'nynch_analytics_opt_out';

export function isAnalyticsOptedOut(): boolean {
  try {
    return window.localStorage.getItem(ANALYTICS_OPT_OUT_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export async function setAnalyticsOptOut(optedOut: boolean): Promise<void> {
  try {
    window.localStorage.setItem(ANALYTICS_OPT_OUT_STORAGE_KEY, optedOut ? 'true' : 'false');
  } catch {
    // Storage unavailable (private mode) — the in-session client calls below still apply.
  }

  if (optedOut) {
    if (isInitialized && mixpanelClient) {
      try {
        mixpanelClient.opt_out_tracking();
      } catch (error) {
        log.error('Mixpanel opt-out failed', error);
      }
    }
    return;
  }

  await initMixpanel();
  if (isInitialized && mixpanelClient) {
    try {
      mixpanelClient.opt_in_tracking();
    } catch (error) {
      log.error('Mixpanel opt-in failed', error);
    }
  }
}

const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC_ID_SEGMENT = /^\d{5,}$/;
const HEX_ID_SEGMENT = /^[0-9a-f]{16,}$/i;

/**
 * Reduce a pathname to a route shape safe for analytics: no query, no hash,
 * and record ids collapsed to ':id' so a page view never names a specific
 * contact, company or deal.
 */
export function sanitizeRoutePath(pathname: string): string {
  const path = (pathname.split('?')[0] ?? '').split('#')[0] ?? '';
  const sanitized = path
    .split('/')
    .map((segment) => {
      if (!segment) return segment;
      if (
        UUID_SEGMENT.test(segment)
        || NUMERIC_ID_SEGMENT.test(segment)
        || HEX_ID_SEGMENT.test(segment)
      ) {
        return ':id';
      }
      return segment;
    })
    .join('/');
  return sanitized || '/';
}

async function loadMixpanelBrowser(): Promise<MixpanelClient | null> {
  if (mixpanelClient) return mixpanelClient;

  mixpanelImportPromise ??= import('mixpanel-browser')
    .then((module) => {
      mixpanelClient = module.default as MixpanelClient;
      return mixpanelClient;
    })
    .catch((error: unknown) => {
      mixpanelImportPromise = null;
      log.error('Mixpanel browser import failed', error);
      return null;
    });

  return mixpanelImportPromise;
}

/**
 * Initialize Mixpanel. Call once at app startup (main.tsx).
 * Skips initialization in dev mode or when token is missing.
 */
export async function initMixpanel() {
  const token = import.meta.env.VITE_MIXPANEL_TOKEN;

  if (!token) {
    log.info('Mixpanel disabled: no token configured');
    return;
  }

  if (isAnalyticsOptedOut()) {
    log.info('Mixpanel disabled: user opted out of usage analytics');
    return;
  }

  try {
    const client = await loadMixpanelBrowser();
    if (!client) return;

    client.init(token, {
      // Privacy: no automatic URL capture. Record ids live in paths and query
      // strings, so raw URLs must never reach analytics. Route views are
      // tracked manually via sanitizeRoutePath (RouteAnalyticsTracker), and
      // the blacklist strips the URL defaults Mixpanel adds to every event.
      track_pageview: false,
      property_blacklist: ['$current_url', '$referrer', '$initial_referrer'],
      persistence: 'localStorage',
      opt_out_tracking_persistence_type: 'localStorage',
      api_host: 'https://api-eu.mixpanel.com',
    });
    isInitialized = true;
    log.info('Mixpanel initialized');
  } catch (error) {
    log.error('Mixpanel init failed', error);
  }
}

/** Guard: skip tracking for demo users or when not initialized. */
function getClient(): MixpanelClient | null {
  if (!isInitialized || isDemoMode()) return null;
  return mixpanelClient;
}

class MixpanelAnalytics {
  trackPQLEvent(eventName: PQLEvent, properties?: Record<string, unknown>) {
    const client = getClient();
    if (!client) return;

    try {
      client.track(eventName, {
        ...properties,
        pql_weight: PQL_WEIGHTS[eventName],
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      log.error('Mixpanel tracking error', error);
    }
  }

  identifyUser(userId: string, properties?: Record<string, unknown>) {
    const client = getClient();
    if (!client) return;

    try {
      client.identify(userId);
      if (properties) {
        client.people.set(properties);
      }
    } catch (error) {
      log.error('Mixpanel identify error', error);
    }
  }

  setUserProperties(properties: Record<string, unknown>) {
    const client = getClient();
    if (!client) return;

    try {
      client.people.set(properties);
    } catch (error) {
      log.error('Mixpanel set properties error', error);
    }
  }

  incrementUserProperty(property: string, value: number = 1) {
    const client = getClient();
    if (!client) return;

    try {
      client.people.increment(property, value);
    } catch (error) {
      log.error('Mixpanel increment error', error);
    }
  }

  trackPageView(pageName: string, properties?: Record<string, unknown>) {
    const client = getClient();
    if (!client) return;

    try {
      client.track_pageview({
        page: pageName,
        ...properties
      });
    } catch (error) {
      log.error('Mixpanel page view error', error);
    }
  }

  calculatePQLScore(eventCounts: Partial<Record<PQLEvent, number>>): PQLScore {
    let score = 0;

    Object.entries(eventCounts).forEach(([event, count]) => {
      const weight = PQL_WEIGHTS[event as PQLEvent] || 0;
      score += weight * count;
    });

    let level: PQLScore['level'] = 'cold';
    if (score >= PQL_THRESHOLDS.qualified) level = 'qualified';
    else if (score >= PQL_THRESHOLDS.hot) level = 'hot';
    else if (score >= PQL_THRESHOLDS.warm) level = 'warm';

    return {
      score,
      level,
      events: eventCounts as Record<PQLEvent, number>
    };
  }

  /** First 30 Days course: a play was opened (its video started). */
  trackAcademyPlayStarted(playId: string) {
    const client = getClient();
    if (!client) return;

    try {
      client.track('academy_play_started', {
        play_id: playId,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      log.error('Mixpanel tracking error', error);
    }
  }

  /** First 30 Days course: a play's real-data action was verified by its milestone. */
  trackAcademyPlayVerified(playId: string, milestoneKey: string) {
    const client = getClient();
    if (!client) return;

    try {
      client.track('academy_play_verified', {
        play_id: playId,
        milestone_key: milestoneKey,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      log.error('Mixpanel tracking error', error);
    }
  }

  trackMilestone(milestone: string, properties?: Record<string, unknown>) {
    const client = getClient();
    if (!client) return;

    try {
      client.track('milestone_reached', {
        milestone,
        ...properties
      });
    } catch (error) {
      log.error('Mixpanel milestone tracking error', error);
    }
  }

  trackFeatureUsage(featureName: string, action: string, properties?: Record<string, unknown>) {
    const client = getClient();
    if (!client) return;

    try {
      client.track('feature_used', {
        feature: featureName,
        action,
        ...properties
      });

      this.trackPQLEvent('feature_explored', { feature: featureName, action });
    } catch (error) {
      log.error('Mixpanel feature tracking error', error);
    }
  }

  trackConversion(conversionType: string, value?: number, properties?: Record<string, unknown>) {
    const client = getClient();
    if (!client) return;

    try {
      client.track('conversion', {
        type: conversionType,
        value,
        ...properties
      });
    } catch (error) {
      log.error('Mixpanel conversion tracking error', error);
    }
  }

  trackAhaReached(ahaType: string, timeToAhaSeconds: number, persona: string | null, pathTaken: Record<string, unknown> = {}) {
    const client = getClient();
    if (!client) return;

    try {
      client.track('aha_moment_reached', {
        aha_type: ahaType,
        time_to_aha_seconds: timeToAhaSeconds,
        persona,
        path_taken: pathTaken,
        timestamp: new Date().toISOString()
      });

      this.incrementUserProperty('aha_moments_reached', 1);
    } catch (error) {
      log.error('Mixpanel aha tracking error', error);
    }
  }

  trackOnboardingStep(step: string, completed: boolean, metadata?: Record<string, unknown>) {
    const client = getClient();
    if (!client) return;

    try {
      client.track('onboarding_step', {
        step,
        completed,
        ...metadata,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      log.error('Mixpanel onboarding tracking error', error);
    }
  }

  reset() {
    const client = getClient();
    if (!client) return;

    try {
      client.reset();
    } catch (error) {
      log.error('Mixpanel reset error', error);
    }
  }
}

// Export singleton instance
export const mixpanel = new MixpanelAnalytics();
